import {
  Box,
  Stack,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  Badge,
  Popover,
  Divider,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
} from "@mui/material";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { useThemeColors } from "../context/ThemeContext";
import api from "../api";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LanguageIcon from "@mui/icons-material/Language";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import ScanStatusIndicator from "./ScanStatusIndicator";

const SEVERITY_STYLES = {
  error: {
    color: "#ef4444",
    bg: "rgba(239, 68, 68, 0.12)",
    icon: <ErrorOutlineOutlinedIcon sx={{ fontSize: 18 }} />,
  },
  warning: {
    color: "#f59e0b",
    bg: "rgba(245, 158, 11, 0.12)",
    icon: <WarningAmberOutlinedIcon sx={{ fontSize: 18 }} />,
  },
  info: {
    color: "#3b82f6",
    bg: "rgba(59, 130, 246, 0.12)",
    icon: <InfoOutlinedIcon sx={{ fontSize: 18 }} />,
  },
  success: {
    color: "#10b981",
    bg: "rgba(16, 185, 129, 0.12)",
    icon: <CheckCircleOutlinedIcon sx={{ fontSize: 18 }} />,
  },
};

function daysUntil(date) {
  if (!date) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((new Date(date) - today) / (1000 * 60 * 60 * 24));
}

function buildNotifications(domains, servers) {
  const items = [];

  domains.forEach((d) => {
    const days = daysUntil(d.expiry);
    if (days === null) {
      items.push({
        id: `domain-noexpiry-${d.id}`,
        severity: "info",
        category: "Domain",
        to: `/domains/${d.id}`,
        icon: <LanguageIcon sx={{ fontSize: 16 }} />,
        title: `${d.name || "Unnamed domain"} has no expiry date`,
        subtitle: "Update the domain record to track renewals",
        priority: 4,
      });
    } else if (days < 0) {
      items.push({
        id: `domain-expired-${d.id}`,
        severity: "error",
        category: "Domain",
        to: `/domains/${d.id}`,
        icon: <LanguageIcon sx={{ fontSize: 16 }} />,
        title: `${d.name} expired ${Math.abs(days)} day${
          Math.abs(days) === 1 ? "" : "s"
        } ago`,
        subtitle: "Renew this domain",
        priority: 1,
      });
    } else if (days <= 30) {
      items.push({
        id: `domain-expiring-${d.id}`,
        severity: "warning",
        category: "Domain",
        to: `/domains/${d.id}`,
        icon: <LanguageIcon sx={{ fontSize: 16 }} />,
        title: `${d.name} expires in ${days} day${days === 1 ? "" : "s"}`,
        subtitle: "Action recommended soon",
        priority: 2,
      });
    }
  });

  servers.forEach((s) => {
    const days = daysUntil(s.expiry);
    if (days !== null && days < 0) {
      items.push({
        id: `server-expired-${s.id}`,
        severity: "error",
        category: "Server",
        to: "/servers",
        icon: <DnsOutlinedIcon sx={{ fontSize: 16 }} />,
        title: `Server ${s.name} expired ${Math.abs(days)}d ago`,
        subtitle: s.hostingProvider || "Renewal needed",
        priority: 1,
      });
    } else if (days !== null && days <= 30) {
      items.push({
        id: `server-expiring-${s.id}`,
        severity: "warning",
        category: "Server",
        to: "/servers",
        icon: <DnsOutlinedIcon sx={{ fontSize: 16 }} />,
        title: `Server ${s.name} expires in ${days}d`,
        subtitle: s.hostingProvider || "Plan renewal",
        priority: 2,
      });
    }
    if (!s.publicIp) {
      items.push({
        id: `server-noip-${s.id}`,
        severity: "info",
        category: "Server",
        to: "/servers",
        icon: <DnsOutlinedIcon sx={{ fontSize: 16 }} />,
        title: `Server ${s.name || "(unnamed)"} is missing a Public IP address`,
        subtitle: "Add the IP to complete the record",
        priority: 4,
      });
    }
  });

  items.sort((a, b) => a.priority - b.priority);

  return {
    items,
    summary: {
      domains: domains.length,
      servers: servers.length,
      errors: items.filter((i) => i.severity === "error").length,
      warnings: items.filter((i) => i.severity === "warning").length,
    },
  };
}

function Header({ onMenuClick }) {
  const navigate = useNavigate();
  const { signOut, currentUser } = useContext(AuthContext);

  // Pretty version of the username for display ("john.doe" → "John.doe")
  const displayName = currentUser?.username
    ? currentUser.username.charAt(0).toUpperCase() + currentUser.username.slice(1)
    : "Admin";
  const displaySubLabel = currentUser?.email
    ? currentUser.email
    : currentUser?.role
    ? currentUser.role === "admin"
      ? "Administrator"
      : "Standard User"
    : "admin@gmail.com";
  const avatarInitial = (currentUser?.username || "A").charAt(0).toUpperCase();
  const isAdminUser = currentUser?.role !== "user";
  const avatarGradient = isAdminUser
    ? "#3B82F6"
    : "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)";
  const colors = useThemeColors();

  const [anchorEl, setAnchorEl] = useState(null);
  const [notifFilter, setNotifFilter] = useState("all");

  // --- Global domain search ---
  const searchRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [allDomains, setAllDomains] = useState([]);

  // Load the domain list once so search works the moment the page opens.
  useEffect(() => {
    let cancelled = false;
    api
      .get("/domains")
      .then((d) => {
        if (!cancelled) setAllDomains(Array.isArray(d) ? d : []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return allDomains
      .filter((d) => (d.name || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [searchQuery, allDomains]);

  function goToDomain(id) {
    setSearchOpen(false);
    setSearchQuery("");
    navigate(`/domains/${id}`);
  }

  // Reset the filter whenever the popover closes
  useEffect(() => {
    if (!anchorEl) setNotifFilter("all");
  }, [anchorEl]);

  // Fetch domains + servers for the notification bell — once on mount (so the
  // badge count shows right away) and again each time the popover is opened.
  // No background polling: this used to refetch every 60s, which spammed
  // /domains + /servers endlessly. Opening the bell gives a fresh read.
  const [notifData, setNotifData] = useState({ domains: [], servers: [] });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [d, s] = await Promise.all([
          api.get("/domains"),
          api.get("/servers"),
        ]);
        if (!cancelled) setNotifData({ domains: d, servers: s });
      } catch {
        // Ignore — header will just show no notifications
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [anchorEl]);

  const { items, summary } = useMemo(
    () => buildNotifications(notifData.domains, notifData.servers),
    [notifData]
  );

  const filteredItems = useMemo(() => {
    switch (notifFilter) {
      case "error":
      case "warning":
      case "info":
        return items.filter((i) => i.severity === notifFilter);
      default:
        return items;
    }
  }, [items, notifFilter]);

  const unreadCount = items.length;

  async function handleLogout() {
    await signOut();
    navigate("/");
  }

  return (
    <Box
      sx={{
        // Opaque surface (menuBg) so the sticky navbar doesn't show the page
        // scrolling through it. cardBg is translucent glass and looked
        // see-through here.
        background: "#ffffff",
        borderBottom: "1px solid #EEF1F6",
        boxShadow: "0 1px 3px rgba(16, 24, 40, 0.04)",
        px: { xs: 1.5, md: 3 },
        py: 1.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "sticky",
        top: 0,
        zIndex: 10,
        transition: "background 0.2s ease, border-color 0.2s ease",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
        {/* Hamburger — opens the mobile sidebar drawer. Hidden on desktop. */}
        <IconButton
          onClick={onMenuClick}
          aria-label="Open menu"
          sx={{
            display: { xs: "inline-flex", md: "none" },
            color: colors.textMuted,
            "&:hover": { background: colors.hover, color: colors.text },
          }}
        >
          <MenuIcon />
        </IconButton>
      </Stack>

      {/* Global domain search — hidden on mobile to free up header space
          (list pages have their own search). Sits on the left now that the
          per-page title has been removed. */}
      <Box
        ref={searchRef}
        sx={{
          flex: 1,
          maxWidth: 460,
          ml: 0,
          mr: "auto",
          display: { xs: "none", md: "block" },
        }}
      >
        <TextField
          fullWidth
          size="small"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setSearchOpen(true);
          }}
          onFocus={() => {
            if (searchQuery.trim()) setSearchOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && searchResults.length > 0) {
              goToDomain(searchResults[0].id);
            } else if (e.key === "Escape") {
              setSearchOpen(false);
            }
          }}
          placeholder="Search domains…"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{ fontSize: 18, color: colors.textMuted }}
                  />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              background: colors.hover,
              color: colors.text,
              borderRadius: 2,
              fontSize: 13.5,
              "& fieldset": { borderColor: colors.cardBorder },
              "&:hover fieldset": { borderColor: colors.cardBorderHover },
              "&.Mui-focused fieldset": { borderColor: "#3B82F6" },
            },
            "& input::placeholder": {
              color: colors.textFaint,
              opacity: 1,
            },
          }}
        />
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        spacing={{ xs: 0.25, md: 1 }}
      >
        {/* Scan status — hidden on mobile to keep the header uncluttered */}
        <Box sx={{ display: { xs: "none", md: "flex" } }}>
          <ScanStatusIndicator />
        </Box>

        <Tooltip title="Notifications">
          <IconButton
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              color: colors.textMuted,
              "&:hover": {
                background: colors.hover,
                color: colors.text,
              },
            }}
          >
            <Badge
              badgeContent={unreadCount}
              max={99}
              sx={{
                "& .MuiBadge-badge": {
                  background:
                    summary.errors > 0
                      ? "#ef4444"
                      : "#3B82F6",
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 700,
                  minWidth: 16,
                  height: 16,
                  padding: "0 4px",
                },
              }}
            >
              <NotificationsNoneOutlinedIcon />
            </Badge>
          </IconButton>
        </Tooltip>

        <Tooltip title="Settings">
          <IconButton
            onClick={() => navigate("/settings")}
            sx={{
              display: { xs: "none", md: "inline-flex" },
              color: colors.textMuted,
              "&:hover": {
                background: colors.hover,
                color: colors.text,
              },
            }}
          >
            <SettingsOutlinedIcon />
          </IconButton>
        </Tooltip>

        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{
            pl: 2,
            ml: 1,
            borderLeft: `1px solid ${colors.cardBorder}`,
          }}
        >
          <Avatar
            sx={{
              width: 36,
              height: 36,
              fontSize: 14,
              fontWeight: 600,
              background: avatarGradient,
            }}
          >
            {avatarInitial}
          </Avatar>
          <Box sx={{ lineHeight: 1.2, minWidth: 0, display: { xs: "none", md: "block" } }}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: colors.text,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 180,
              }}
            >
              {displayName}
            </Typography>
            <Typography
              sx={{
                fontSize: 11,
                color: colors.textMuted,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 180,
              }}
            >
              {displaySubLabel}
            </Typography>
          </Box>
        </Stack>

        <Tooltip title="Logout">
          <IconButton
            onClick={handleLogout}
            sx={{
              color: colors.textMuted,
              "&:hover": {
                background: "rgba(239, 68, 68, 0.1)",
                color: "#ef4444",
              },
            }}
          >
            <LogoutOutlinedIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Global search results */}
      <Popover
        open={searchOpen && searchQuery.trim().length > 0}
        anchorEl={searchRef.current}
        onClose={() => setSearchOpen(false)}
        disableAutoFocus
        disableEnforceFocus
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: searchRef.current ? searchRef.current.offsetWidth : 360,
              borderRadius: 2.5,
              background: colors.darkMode ? "#1e293b" : "#ffffff",
              border: `1px solid ${colors.cardBorder}`,
              boxShadow: colors.darkMode
                ? "0 20px 50px rgba(0,0,0,0.5)"
                : "0 20px 50px rgba(0,0,0,0.12)",
              overflow: "hidden",
            },
          },
        }}
      >
        {searchResults.length === 0 ? (
          <Box
            sx={{
              py: 3,
              px: 2.5,
              textAlign: "center",
              color: colors.textMuted,
            }}
          >
            <SearchIcon sx={{ fontSize: 28, color: colors.textFaint, mb: 0.5 }} />
            <Typography sx={{ fontSize: 13 }}>
              No domains found for “{searchQuery.trim()}”
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 360, overflowY: "auto" }}>
            {searchResults.map((d) => (
              <Stack
                key={d.id}
                direction="row"
                alignItems="center"
                spacing={1.5}
                onClick={() => goToDomain(d.id)}
                sx={{
                  px: 2,
                  py: 1.25,
                  cursor: "pointer",
                  transition: "background 0.15s ease",
                  "&:hover": { background: colors.hover },
                }}
              >
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: 1.5,
                    background: "rgba(129, 140, 248, 0.12)",
                    color: "#3B82F6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <LanguageIcon sx={{ fontSize: 16 }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 13.5,
                      fontWeight: 600,
                      color: colors.text,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {d.name}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: colors.textMuted }}>
                    {d.expiry
                      ? `Expires ${String(d.expiry).slice(0, 10)}`
                      : "No expiry set"}
                    {d.status ? ` · ${d.status}` : ""}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Box>
        )}
      </Popover>

      {/* Notification popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: 380,
              maxWidth: "92vw",
              borderRadius: 3,
              // Solid (opaque) surface so the page behind doesn't bleed through
              // the notification list. cardBg is translucent glass, which looks
              // see-through here, so use a fully-opaque colour instead.
              background: colors.darkMode ? "#1e293b" : "#ffffff",
              border: `1px solid ${colors.cardBorder}`,
              boxShadow: colors.darkMode
                ? "0 20px 50px rgba(0,0,0,0.5)"
                : "0 20px 50px rgba(0,0,0,0.12)",
              overflow: "hidden",
            },
          },
        }}
      >
        {/* Filter header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            boxSizing: "border-box",
            px: 1.5,
            py: 1.25,
            borderBottom: `1px solid ${colors.divider}`,
            gap: 1,
          }}
        >
          <Select
            value={notifFilter}
            onChange={(e) => setNotifFilter(e.target.value)}
            size="small"
            startAdornment={
              <FilterListIcon
                sx={{ fontSize: 16, color: colors.textMuted, mr: 0.75 }}
              />
            }
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: colors.text,
              background: colors.cardBg,
              flex: 1,
              minWidth: 0,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: colors.cardBorder,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: colors.cardBorderHover,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#3B82F6",
              },
              "& .MuiSvgIcon-root.MuiSelect-icon": {
                color: colors.textMuted,
              },
            }}
          >
            <MenuItem value="all" sx={{ fontSize: 13 }}>
              All notifications
            </MenuItem>
            <MenuItem value="error" sx={{ fontSize: 13 }}>
              Expired Domains
            </MenuItem>
            <MenuItem value="warning" sx={{ fontSize: 13 }}>
              Upcoming Expiry
            </MenuItem>
            <MenuItem value="info" sx={{ fontSize: 13 }}>
              Missing Details
            </MenuItem>
          </Select>

          <Box
            sx={{
              flexShrink: 0,
              px: 1.25,
              py: 0.4,
              borderRadius: 1.5,
              background: colors.hover,
              fontSize: 11,
              fontWeight: 700,
              color: colors.textMuted,
              whiteSpace: "nowrap",
            }}
          >
            {filteredItems.length} total
          </Box>
        </Box>

        {/* List */}
        <Box sx={{ maxHeight: 360, overflowY: "auto" }}>
          {items.length === 0 ? (
            <Box
              sx={{
                py: 5,
                px: 3,
                textAlign: "center",
                color: colors.textMuted,
              }}
            >
              <CheckCircleOutlinedIcon
                sx={{ fontSize: 40, color: "#10b981", mb: 1 }}
              />
              <Typography
                sx={{ fontSize: 14, fontWeight: 600, color: colors.text }}
              >
                You're all caught up
              </Typography>
              <Typography sx={{ fontSize: 12, mt: 0.5 }}>
                No errors, expiries, or data issues
              </Typography>
            </Box>
          ) : filteredItems.length === 0 ? (
            <Box
              sx={{
                py: 4,
                px: 3,
                textAlign: "center",
                color: colors.textMuted,
              }}
            >
              <FilterListIcon
                sx={{ fontSize: 32, color: colors.textFaint, mb: 1 }}
              />
              <Typography
                sx={{ fontSize: 13, fontWeight: 600, color: colors.text }}
              >
                No notifications in this category
              </Typography>
              <Typography sx={{ fontSize: 12, mt: 0.5 }}>
                Try a different filter
              </Typography>
            </Box>
          ) : (
            filteredItems.map((item, idx) => {
              const sev = SEVERITY_STYLES[item.severity];
              return (
                <Box key={item.id}>
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="flex-start"
                    onClick={() => {
                      if (item.to) {
                        setAnchorEl(null);
                        navigate(item.to);
                      }
                    }}
                    sx={{
                      px: 2.5,
                      py: 1.75,
                      cursor: "pointer",
                      transition: "background 0.15s ease",
                      "&:hover": { background: colors.hover },
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1.5,
                        background: sev.bg,
                        color: sev.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        mt: 0.25,
                      }}
                    >
                      {sev.icon}
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mb: 0.25 }}
                      >
                        <Box
                          sx={{
                            color: colors.textMuted,
                            display: "flex",
                          }}
                        >
                          {item.icon}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            color: colors.textMuted,
                          }}
                        >
                          {item.category}
                        </Typography>
                      </Stack>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: colors.text,
                          lineHeight: 1.35,
                        }}
                      >
                        {item.title}
                      </Typography>
                      {item.subtitle && (
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: colors.textMuted,
                            mt: 0.25,
                          }}
                        >
                          {item.subtitle}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                  {idx < filteredItems.length - 1 && (
                    <Divider sx={{ borderColor: colors.divider }} />
                  )}
                </Box>
              );
            })
          )}
        </Box>

        {/* Footer */}
        {items.length > 0 && (
          <Box
            sx={{
              borderTop: `1px solid ${colors.divider}`,
              px: 2.5,
              py: 1.25,
              textAlign: "center",
            }}
          >
            <Typography
              onClick={() => {
                setAnchorEl(null);
                navigate("/domains");
              }}
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: "#3B82F6",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              View all domains →
            </Typography>
          </Box>
        )}
      </Popover>
    </Box>
  );
}

export default Header;
